package com.essntl.features.proposal.data.supabase.proposalservice

import com.essntl.features.currency.data.supabase.CurrencyDto
import com.essntl.features.proposal.data.repository.ProposalRepositoryImpl
import com.essntl.features.proposal.domain.model.ProposalServiceModel
import com.essntl.features.service.data.supabase.service.ServiceDto
import com.essntl.features.supplier.data.supabase.SupplierDto
import io.github.jan.supabase.postgrest.query.Columns
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/**
 * If you edited these fields make sure [ProposalRepositoryImpl.addAllProposalService]
 */
@Serializable
data class ProposalServiceDto(
    val id: String,
    @SerialName("proposal_id")
    val proposalId: String?,
    @SerialName("service_id_data")
    val service: ServiceDto,
    val grouped: Boolean,
    @SerialName("additional_info")
    val additionalInfo: String?,
    @SerialName("edited_description")
    val editedDescription: String?,
    @SerialName("start_date")
    val startDate: String,
    @SerialName("end_date")
    val endDate: String?,
    @SerialName("start_time")
    val startTime: String?,
    @SerialName("end_time")
    val endTime: String?,
    val index: Int,
    val status: String?,
    @SerialName("created_at")
    val createdAt: String,
    @SerialName("updated_at")
    val updatedAt: String?,
    val price: Int?,
    val cost: Int?,
    @SerialName("internal_notes")
    val internalNotes: String?,
    @SerialName("supplier_id_data")
    val supplier: SupplierDto?,
    @SerialName("guest_count")
    val guestCount: Int,
    @SerialName("updated_by")
    val updatedBy: String?,
    val clients: List<String>?,
    @SerialName("itinerary_id")
    val itineraryId: String?,
    @SerialName("dynamic_fields")
    val dynamicFields: Map<String, String>?,
    @SerialName("special_notes")
    val specialNotes: String?,
    val paid: Boolean?,
    @SerialName("embedded_map")
    val embeddedMap: String?,
    @SerialName("embedded_content")
    val embeddedContent: String?,
    val files: List<String>?,
    val commissionable: Boolean?,
    @SerialName("booked_by_ta")
    val bookedByTa: Boolean?,
    @SerialName("dynamic_price_enabled")
    val dynamicPriceEnabled: Boolean?,
    val archived: Boolean?,
    @SerialName("price_currency_data")
    val priceCurrency: CurrencyDto?,
    @SerialName("cost_currency_data")
    val costCurrency: CurrencyDto?,
    @SerialName("hide_on_proposal")
    val hideOnProposal: Boolean?
) {
    fun toModel() =
        ProposalServiceModel(
            id = id,
            proposalId = proposalId,
            service = service.toModel(),
            grouped = grouped,
            additionalInfo = additionalInfo,
            editedDescription = editedDescription,
            startDate = startDate,
            endDate = endDate,
            startTime = startTime,
            endTime = endTime,
            index = index,
            createdAt = createdAt,
            updatedAt = updatedAt,
            price = price,
            cost = cost,
            supplier = supplier?.toModel(),
            internalNotes = internalNotes,
            guestCount = guestCount,
            updatedBy = updatedBy,
            clients = clients,
            itineraryId = itineraryId,
            dynamicFields = dynamicFields,
            specialNotes = specialNotes,
            paid = paid,
            embeddedMap = embeddedMap,
            embeddedContent = embeddedContent,
            files = files?.toSet(),
            commissionable = commissionable,
            bookedByTa = bookedByTa,
            archived = archived,
            priceCurrency = priceCurrency?.toModel(),
            costCurrency = costCurrency?.toModel(),
            status =
                when (status) {
                    "READY" -> ProposalServiceModel.Status.Ready
                    "PO_SENT" -> ProposalServiceModel.Status.POSent
                    "CONFIRMED" -> ProposalServiceModel.Status.Confirmed
                    "TO_CHANGE" -> ProposalServiceModel.Status.TOChange
                    "CANCELLED" -> ProposalServiceModel.Status.Cancelled
                    "COMPLETED" -> ProposalServiceModel.Status.Completed
                    else -> null
                },
            dynamicPriceEnabled = dynamicPriceEnabled ?: false,
            hideOnProposal = hideOnProposal,
        )

    companion object {
        const val COLUMNS_VALUE =
            "*, supplier_id_data:supplier_id(${SupplierDto.COLUMNS_VALUE}), service_id_data:service_id(${ServiceDto.COLUMNS_VALUE})" +
                ", price_currency_data:price_currency(*)" +
                ", cost_currency_data:cost_currency(*)"

        fun getColumns() = Columns.raw(COLUMNS_VALUE)
    }
}

internal fun ProposalServiceModel.Status.toDto() =
    when (this) {
        ProposalServiceModel.Status.Ready -> "READY"
        ProposalServiceModel.Status.POSent -> "PO_SENT"
        ProposalServiceModel.Status.Confirmed -> "CONFIRMED"
        ProposalServiceModel.Status.TOChange -> "TO_CHANGE"
        ProposalServiceModel.Status.Cancelled -> "CANCELLED"
        ProposalServiceModel.Status.Completed -> "COMPLETED"
    }